import { Store } from '@ngrx/store';
import { orderBy, uniq } from 'lodash-es';
import {
  CommentInput,
  ConversationEntityInput,
} from 'projects/semeris/src/generated/graphql-models';
import { ConversationActions } from 'projects/semeris/src/store/conversation/conversation.actions';
import { RouterSelectors } from 'projects/semeris/src/store/router/router.selector';
import { TenantUserActions } from 'projects/semeris/src/store/tenant-user/tenant-user.actions';
import { TenantUserSelectors } from 'projects/semeris/src/store/tenant-user/tenant-user.selectors';
import { Observable } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mapTo,
  skipWhile,
  switchMap,
  take,
} from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { LabelActions } from '../../../store/label/label.actions';
import { LabelSelectors } from '../../../store/label/label.selectors';
import { EXAMPLE_LABELS } from '../../guided-tour/constants/exmple-entities/example-labels';
import { EXAMPLE_NOTES } from '../../guided-tour/constants/exmple-entities/example-notes';
import { EXAMPLE_USERS } from '../../guided-tour/constants/exmple-entities/example-users';
import { LabelCategories } from '../../label/constants/label-categories';
import { Label } from '../../label/models/label.model';
import { LabelService } from '../../label/services/label.service';
import { FilterValue } from '../../shared/components/filter-by-input/models/filter-by-input.model';
import { WebSocketEntities } from '../../shared/models/web-socket/web-socket.models';
import { WebSocketService } from '../../shared/services/web-socket/web-socket.service';
import { EntityListState, ListStore } from '../../shared/store/list-store';
import { arrayToRecord } from '../../shared/utils/array-to-record';
import { CrudOptions } from '../../unified-crud/utils/query-serializer';
import { OtherUser } from '../../user/services/tenant-user.service';
import { PendingConversation } from '../components/conversation-input/conversation-input.component';
import {
  ConversationAnchor,
  ConversationViewModel,
  ConversationViewModelWithLabels,
} from '../models/conversation.model';
import { CommentService } from '../services/comment.service';
import { ConversationService } from '../services/conversation.service';

export interface ConversationListState extends EntityListState<ConversationViewModel> {
  startDate?: Date;
  filterAnchors?: ConversationAnchor[];
  showDeletedNotes?: boolean;
}

export abstract class ConversationListStore<
  TState extends ConversationListState = ConversationListState,
> extends ListStore<ConversationViewModel, TState> {
  protected FIND_IN: (keyof ConversationViewModel)[] = null;

  private readonly mockedGuidedTourIds = ['note-block', 'note-actions', 'notes-page'];

  public showDeletedNotes$ = this.select((state) => state.showDeletedNotes);

  public labels$ = this.select(
    this.store.select(RouterSelectors.selectQueryParam('guidedTourId')),
    this.store.select(LabelSelectors.getLabelsByCategory(LabelCategories.Notes)),
    (guidedTourId, labels) =>
      this.mockedGuidedTourIds.includes(guidedTourId as string) ? (EXAMPLE_LABELS as any) : labels
  );

  public users$: Observable<OtherUser[]> = this.select(
    this.store.select(RouterSelectors.selectQueryParam('guidedTourId')),
    this.store.select(TenantUserSelectors.users),
    (guidedTourId, users) =>
      this.mockedGuidedTourIds.includes(guidedTourId as string) ? EXAMPLE_USERS : users
  );

  public readonly viewModels$: Observable<ConversationViewModelWithLabels[]> = this.select(
    this.store.select(RouterSelectors.selectQueryParam('guidedTourId')),
    this.entities$,
    this.store.select(LabelSelectors.labelsLookup).pipe(skipWhile((l) => !l)),
    (guidedTourId, entities, labelLookup) => {
      if (this.mockedGuidedTourIds.includes(guidedTourId as string)) {
        return EXAMPLE_NOTES as any;
      }

      const viewModels = orderBy(entities, (e) => e.createdAt, 'asc').map(
        (e) =>
          ({
            ...e,
            labels: uniq(
              orderBy(
                e.labels?.map((labelId) => labelLookup[labelId]).filter((l) => !!l),
                (l) => l.displayName.toLowerCase(),
                ['asc']
              )
            ),
          }) as ConversationViewModelWithLabels
      );

      return viewModels;
    },
    { debounce: true }
  );

  public rawEntitiesFn(state: ConversationListState): ConversationViewModel[] {
    return state?.entities?.filter((e) => state?.showDeletedNotes || !e?.isDeleted);
  }

  constructor(
    store: Store,
    webSocketService: WebSocketService,
    protected conversationService: ConversationService,
    protected commentService: CommentService,
    protected labelServce: LabelService
  ) {
    super(
      store,
      webSocketService,
      {
        active: 'createdAt',
        direction: 'desc',
      },
      WebSocketEntities.Conversation
    );

    store.dispatch(LabelActions.DownloadLabels());
    store.dispatch(TenantUserActions.LoadTenantUsers({}));
  }

  public onComment(comment: string | PendingConversation) {
    const conversationId = uuid();

    if (!comment) {
      return;
    }

    this.store
      .select(LabelSelectors.defaultLabelIds)
      .pipe(
        take(1),
        switchMap((labelIds) => {
          let commentInput: CommentInput = null;
          let conversationInput: ConversationEntityInput = null;

          if (typeof comment === 'string') {
            commentInput = {
              entity: {
                conversationId,
                html: comment,
                markdown: comment,
              },
            };

            conversationInput = {
              conversationId,
              labels: labelIds,
            };
          } else {
            commentInput = {
              entity: {
                conversationId,
                html: comment.message,
                markdown: comment.message,
              },
            };

            conversationInput = {
              conversationId,
              contextSource: comment.contextSource,
              snapshot: comment.context,
              anchors: comment.anchors,
              labels: comment.labels?.map((l) => l.id),
              assignees: comment.assignees,
            };
          }
          return this.commentService.addComment(commentInput, conversationInput);
        }),
        this.report('Comment added')
      )
      .subscribe(() => {
        this.store.dispatch(ConversationActions.ClearPendingConversation());
        this.loadList();
      });
  }

  public setLabels(conversationIds: string[], labelIds: string[]) {
    const idLookup = arrayToRecord(this.get().entities, 'conversationId');
    this.setLabelsByIds(
      this.labelServce
        .setLabels(
          labelIds,
          conversationIds.map((c) => idLookup[c]._id),
          'conversation'
        )
        .pipe(
          take(1),
          this.report('Labels assigned successfully'),
          mapTo({ labelIds, conversationIds })
        )
    );
  }

  public assignLabels(conversationIds: string[], labelIds: string[]) {
    const idLookup = arrayToRecord(this.get().entities, 'conversationId');
    this.assignLabelByIds(
      this.labelServce
        .assignLabels(
          labelIds,
          conversationIds.map((c) => idLookup[c]._id),
          'conversation'
        )
        .pipe(
          take(1),
          this.report('Labels assigned successfully'),
          mapTo({ labelIds, conversationIds })
        )
    );
  }

  public unassignLabels(conversationIds: string[], labelIds: string[]) {
    const idLookup = arrayToRecord(this.get().entities, 'conversationId');
    this.unassingLabelByIds(
      this.labelServce
        .removeLabels(
          labelIds,
          conversationIds.map((c) => idLookup[c]._id),
          'conversation'
        )
        .pipe(
          take(1),
          this.report('Labels have been removed successfully'),
          mapTo({ labelIds, conversationIds })
        )
    );
  }

  public assignLabel(id: string, labelId: string) {
    this.assignLabelById(
      this.labelServce
        .assignLabel(labelId, id, 'conversation')
        .pipe(take(1), this.report('Label assigned successfully'), mapTo({ labelId, id }))
    );
  }

  public unassignLabel(id: string, labelId: string) {
    this.unassignLabelById(
      this.labelServce
        .removeLabel(labelId, id, 'conversation')
        .pipe(take(1), this.report('Label removed successfully'), mapTo({ labelId, id }))
    );
  }

  public fetchAnchors() {
    this.filterAnchors(this.conversationService.getConversationAnchors());
  }

  public addLabel(label: Label) {
    this.store.dispatch(LabelActions.AddLabel({ label }));
  }

  private readonly assignLabelById = this.updater(
    (state, { labelId, id }: { labelId: string; id: string }): TState => {
      const conversationIndex = state.entities.findIndex((n) => n._id === id);

      if (conversationIndex === -1) {
        return state;
      }

      const conversation = { ...state.entities[conversationIndex] };

      conversation.labels = uniq([...(conversation.labels || []), labelId]);

      const entities = [...state.entities];
      entities[conversationIndex] = conversation;

      return {
        ...state,
        entities,
      };
    }
  );

  private readonly unassignLabelById = this.updater(
    (state, { labelId, id }: { labelId: string; id: string }): TState => {
      const conversationIndex = state.entities.findIndex((n) => n._id === id);

      if (conversationIndex === -1) {
        return state;
      }

      const conversation = { ...state.entities[conversationIndex] };

      conversation.labels = conversation?.labels?.filter((l) => l !== labelId);

      const entities = [...state.entities];
      entities[conversationIndex] = conversation;

      return {
        ...state,
        entities,
      };
    }
  );

  private readonly assignLabelByIds = this.updater(
    (state, { labelIds, conversationIds }: { labelIds: string[]; conversationIds: string[] }) => {
      const conversationLookup = arrayToRecord(conversationIds);

      const entities = state.entities.map((e) => {
        if (conversationLookup[e.conversationId]) {
          e.labels = uniq([...(e.labels || []), ...labelIds]);
        }

        return e;
      });

      return {
        ...state,
        entities,
      };
    }
  );

  private readonly unassingLabelByIds = this.updater(
    (state, { labelIds, conversationIds }: { labelIds: string[]; conversationIds: string[] }) => {
      const conversationLookup = arrayToRecord(conversationIds);

      const entities = state.entities.map((e) => {
        if (conversationLookup[e.conversationId]) {
          e.labels = e?.labels?.filter((l) => !labelIds.includes(l));
        }

        return e;
      });

      return {
        ...state,
        entities,
      };
    }
  );

  private readonly setLabelsByIds = this.updater(
    (state, { labelIds, conversationIds }: { labelIds: string[]; conversationIds: string[] }) => {
      const conversationLookup = arrayToRecord(conversationIds);

      const entities = state.entities.map((e) => {
        if (conversationLookup[e.conversationId]) {
          e.labels = labelIds;
        }

        return e;
      });

      return {
        ...state,
        entities,
      };
    }
  );

  private readonly filterAnchors = this.updater((state, filterAnchors: ConversationAnchor[]) => {
    return {
      ...state,
      filterAnchors,
    };
  });

  protected filterEntitiesByProperty(
    filter: FilterValue,
    entities: ConversationViewModel[]
  ): ConversationViewModel[] {
    // we do on BE side
    return entities;
  }

  protected filterEffect$ = this.effect((input$: Observable<CrudOptions>) => {
    return input$.pipe(
      distinctUntilChanged(),
      debounceTime(400),
      switchMap((crudOptions) => this.fetchList()),
      map((data) => {
        this.patchState({
          entities: data,
        } as TState);
      })
    );
  });
}
